import { useContext, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Autocomplete, Box, InputAdornment, TextField, Typography } from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import UserContext from 'src/context/UserContext';
import { brand } from 'src/theme/brand';
import { NavItem, navigationFor } from '../navigation';

type Option = NavItem & { section: string };

/** Jump to any page the signed-in role can open. Focus with Ctrl/⌘ + K. */
function QuickSearch() {
  const [user] = useContext(UserContext);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const options = useMemo<Option[]>(
    () => navigationFor(user?.role).flatMap((section) => section.items.map((item) => ({ ...item, section: section.title }))),
    [user?.role]
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (options.length === 0) return null;

  return (
    <Autocomplete<Option, false, false, false>
      options={options}
      groupBy={(option) => option.section}
      getOptionLabel={(option) => option.label}
      value={null}
      blurOnSelect
      clearOnBlur
      onChange={(_, option) => option && navigate(option.to)}
      sx={{ width: { sm: 280, md: 340 } }}
      popupIcon={null}
      renderOption={(props, option) => {
        const Icon = option.icon;
        return (
          <Box component="li" {...props} key={option.to} sx={{ gap: 1.25, fontSize: 13.5 }}>
            <Icon sx={{ fontSize: 18, color: brand.muted }} />
            {option.label}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          placeholder="Quick search"
          inputProps={{ ...params.inputProps, 'aria-label': 'Search pages' }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start" sx={{ ml: 0.5 }}>
                <SearchRoundedIcon sx={{ fontSize: 19, color: brand.muted }} />
              </InputAdornment>
            ),
            endAdornment: (
              <Typography
                component="kbd"
                sx={{
                  display: { xs: 'none', md: 'block' },
                  mr: 0.5,
                  px: 0.75,
                  py: 0.1,
                  fontSize: 11,
                  fontFamily: 'inherit',
                  color: brand.muted,
                  border: `1px solid ${brand.border}`,
                  borderRadius: '6px'
                }}
              >
                Ctrl K
              </Typography>
            ),
            sx: {
              borderRadius: '12px',
              backgroundColor: brand.surface,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: brand.border },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: brand.green }
            }
          }}
        />
      )}
    />
  );
}

export default QuickSearch;
